import React, { useState, useMemo, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { IdType, SortingRule } from "react-table";
import { HvGrid } from "@hitachivantara/uikit-react-core";

import {
  HvTableColumnConfig,
  useHvTable,
  useHvSortBy,
  useHvPagination,
  HvCellProps,
} from "@hitachivantara/uikit-react-lab";

import SimpleTable, { textColumn } from "components/common/SimpleTable";

import { Delete } from "@hitachivantara/uikit-react-icons";
import { useFormikContext } from "formik";
import { UserDeviceAttribute } from "lib/api";

export interface DevicesProps {
  initialPageSize?: number;
  setRemovedDevices?: React.Dispatch<
    React.SetStateAction<UserDeviceAttribute[]>
  >;
  removedDevices?: UserDeviceAttribute[];
}

const DevicesTable: React.FC<DevicesProps> = ({
  initialPageSize = 10,
  setRemovedDevices,
  removedDevices,
}) => {
  const { t } = useTranslation("userAddEdit");
  const [tableData, setTableData] = useState<DeviceDataRow[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page] = useState(0);
  const { values, setFieldValue } = useFormikContext<UserDetailsFormData>();
  const [selectedIds, setSelectedIds] = useState<
    Record<IdType<DeviceDataRow>, boolean>
  >({});
  const [size] = useState(initialPageSize);

  const deleteDev = useCallback(
    (id: string, name: string) => {
      const filtereDevices = values.devices?.filter((d) => d.device_id !== id);
      if (removedDevices && setRemovedDevices)
        setRemovedDevices([...removedDevices, { id, name }]);
      setFieldValue("devices", filtereDevices);
    },
    [removedDevices, setFieldValue, setRemovedDevices, values.devices]
  );

  const columns = useMemo(
    (): HvTableColumnConfig<DeviceDataRow>[] => [
      textColumn({
        Header: t("devices.columns.name") as string,
        accessor: "name",
        style: { width: "2%" },
      }),
      textColumn({
        Header: t("devices.columns.id") as string,
        accessor: "id",
        style: { width: "5%" },
      }),
      {
        id: "actions",
        variant: "actions",
        style: { width: "0.4%" },
        Cell: ({ row }: HvCellProps<DeviceDataRow, boolean>) => {
          return (
            <Delete
              style={{
                cursor: "pointer",
                display: "inline-flex",
                marginLeft: "25%",
              }}
              onClick={() => deleteDev(row.original.id, row.original.name)}
            />
          );
        },
      },
    ],
    [deleteDev, t]
  );

  const [sortBy] = useState<SortingRule<MemberDataRow>[]>([
    {
      id: "name",
      desc: false,
    },
  ]);

  useEffect(() => {
    if (values.devices) {
      const data = values.devices.map((device) => ({
        id: device.device_id,
        name: device.device_name,
      })) as DeviceDataRow[];
      setTableData(data);
      setTotalCount(values.devices?.length || 0);
    }
  }, [values.devices]);

  const initialState = useMemo(() => {
    const entries = tableData.map<[string, boolean]>((v) => [
      v.id.toString(),
      !v.is_integration,
    ]);
    return {
      pageIndex: page,
      pageSize: size,
      sortBy,
      lockedSelectionRowIds: entries ? Object.fromEntries(entries) : {},
      selectedRowIds: selectedIds,
    };
  }, [tableData, page, size, sortBy, selectedIds]);

  const instance = useHvTable<DeviceDataRow>(
    {
      columns,
      data: tableData || [],
      getRowId: useCallback((v: DeviceDataRow): string => `${v.id}`, []),
      pageCount: Math.ceil((totalCount ?? 0) / (initialState?.pageSize ?? 1)),
      initialState,
      disableMultiSort: true,
      disableSortRemove: true,
      disableCreateExpandButton: true,
    },

    useHvSortBy,
    useHvPagination
  );

  const {
    state: { selectedRowIds },
  } = instance;
  useEffect(() => setSelectedIds(selectedRowIds), [selectedRowIds]);

  return (
    <HvGrid item container spacing={2}>
      <HvGrid item xs={12}>
        <SimpleTable
          instance={instance}
          totalCount={totalCount}
          hideSelectAllPages
          disableSelection
        />
      </HvGrid>
    </HvGrid>
  );
};
export default DevicesTable;
